import { Button, Dialog, IconChevronDown } from '@madpaws/design-system';
import { useFormikContext } from 'formik';
import { useState } from 'react';

import { DEFAULT_PRICE_RANGE } from '~/common/constants/search';
import { trackEvent } from '~/components/analytics/analytics';
import { UPDATE_PRICE_FILTER_SITTERS } from '~/components/analytics/constants';
import { DOM_CONTAINER_ID } from '~/components/constants';
import { getEventAllProperties } from '~/components/pages/SearchResults/tracking/getUpdatedSearchTrackingProperties';

import styles from './AdvancedFiltersPriceDialog.module.css';
import { getIsPriceFilterChanged } from './utils';
import { SliderField } from '../../ui/SliderField/SliderField';

import type { ReactElement } from 'react';
import type { SearchFilters } from '~/common/types/search';

type Props = {
  handleAdvancedFiltersSubmit: () => void;
  isLoading: boolean;
};

const AdvancedFiltersPriceDialog = ({
  handleAdvancedFiltersSubmit,
  isLoading,
}: Props): ReactElement => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const { setFieldValue, initialValues, values } = useFormikContext<SearchFilters>();

  const handleFilterClear = (): void => {
    setFieldValue('advancedFilters.price', DEFAULT_PRICE_RANGE);
  };

  const handleFilterReset = (): void => {
    const price = initialValues.advancedFilters?.price ?? DEFAULT_PRICE_RANGE;

    setFieldValue('advancedFilters.price', price);
  };

  const handleDialogToggle = (): void => {
    setIsDialogOpen(!isDialogOpen);
  };

  const handleDialogDismiss = (): void => {
    handleFilterReset();
    handleDialogToggle();
  };

  const handleFiltersDialogSearch = (): void => {
    const eventAllProperties = getEventAllProperties(values);

    trackEvent(UPDATE_PRICE_FILTER_SITTERS, eventAllProperties);

    handleDialogToggle();
    handleAdvancedFiltersSubmit();
  };

  const isChanged = getIsPriceFilterChanged(values.advancedFilters);

  const getFilterButtonLabel = (): string => {
    const price = values.advancedFilters?.price ?? DEFAULT_PRICE_RANGE;

    if (!isChanged) {
      return 'Price';
    }

    return `$${price[0]}-$${price[1]}`;
  };

  return (
    <div className={styles.root}>
      <div className={`${styles.price} ${isChanged && styles.changed}`}>
        <Button
          iconTrailing={<IconChevronDown size="small2x" />}
          isDisabled={isLoading}
          isFullBleed
          label={getFilterButtonLabel()}
          onClick={handleDialogToggle}
          size="small1x"
          variant="tertiary"
        />
      </div>
      <Dialog
        cta={{
          size: 'large1x',
          label: 'Show Sitters',
          onClick: handleFiltersDialogSearch,
          isDisabled: isLoading,
        }}
        domContainerId={DOM_CONTAINER_ID}
        heading="Price"
        isOpen={isDialogOpen}
        onRequestClose={handleDialogDismiss}
        secondaryAction={{
          label: 'Clear',
          onClick: handleFilterClear,
        }}
      >
        <SliderField max={300} name="advancedFilters.price" />
      </Dialog>
    </div>
  );
};

export { AdvancedFiltersPriceDialog };
